// ─────────────────────────────────────────────────────────────
//  DEV BLOG POSTS
//  Add "content" as an array of paragraphs for the full article.
//  Leave content empty ([]) and the site shows a "coming soon" state.
// ─────────────────────────────────────────────────────────────

const devPosts = [
  {
    id: "d1",
    slug: "things-i-wish-i-knew-before-first-dev-job",
    title: "Things I Wish I Knew Before My First Dev Job",
    emoji: "💼",
    category: "Career",
    tags: ["career", "beginners", "advice", "junior-dev"],
    date: "Apr 2026",
    featured: true,
    readTime: "5 min read",
    description:
      "The soft skills, mindset shifts and practical habits nobody tells you about when you're just starting out.",
    color: "#e8eeff",
    accentColor: "#4a5fe8",
    content: [
      "Nobody tells you that the most important skill in your first dev job isn't coding - it's asking good questions. Specifically, knowing when to ask and how to ask them without making it seem like you didn't try first.",
      "The rule I live by: spend 30 minutes genuinely trying to solve something yourself. Document what you tried. Then ask, with context. 'I've tried X and Y, getting error Z, here's my code' gets you help in 2 minutes. 'My code doesn't work' gets ignored.",
      "Your PRs will get destroyed in code review. This is not an attack on your intelligence - it's how you become a better developer in 3 months instead of 3 years. Separate your ego from your code early.",
      "Read the codebase before you write a single line. Understand the patterns already established. Your first few months should be about understanding, not rewriting.",
      "And finally: write things down. Your future self at 3pm on a Friday will thank the you who documented that weird deployment fix at 9am on a Tuesday.",
    ],
  },
  {
    id: "d2",
    slug: "react-hooks-i-use-every-single-day",
    title: "React Hooks I Actually Use Every Day",
    emoji: "⚛️",
    category: "React",
    tags: ["react", "hooks", "frontend", "productivity"],
    date: "Mar 2026",
    featured: true,
    readTime: "7 min read",
    description:
      "Not a tutorial - just an honest list of the ones that made my code cleaner and my life easier.",
    color: "#e0f8ff",
    accentColor: "#0ea5c4",
    content: [
      "useState and useEffect are obvious. Let me tell you about the ones that actually changed how I write components.",
      "useReducer is my most underrated pick. The moment your useState has more than 3 related values that update together, switch to useReducer. The reducer pattern makes your state transitions explicit and debuggable.",
      "useCallback and useMemo - but carefully. I see these everywhere, often unnecessarily. Use useCallback when you're passing a function to a memoized child component or when it's a dependency in a useEffect. Use useMemo for genuinely expensive computations. That's it.",
      "Custom hooks are where the real power is. The moment I started extracting logic into custom hooks (useFetch, useLocalStorage, useDebounce), my components became so much more readable. A component should tell a story, not manage state.",
      "useRef for more than just DOM refs. I use it to store any mutable value that doesn't need to trigger a re-render - previous values, timers, flag variables. It's basically a class instance variable for function components.",
    ],
  },
  {
    id: "d3",
    slug: "managing-side-projects-full-time-job",
    title: "Managing Side Projects With a Full-Time Job",
    emoji: "⚡",
    category: "Productivity",
    tags: ["productivity", "side-projects", "work-life-balance"],
    date: "Feb 2026",
    featured: false,
    readTime: "4 min read",
    description:
      "The systems I've built (and broken and rebuilt) to keep creating without burning out.",
    color: "#fff0e8",
    accentColor: "#e8622a",
    content: [
      "I've started and abandoned about 10 side projects. The ones I've actually shipped all had one thing in common: an unreasonably small scope.",
      "The biggest lie I told myself: 'I'll work on it every day.' I don't. Some weeks are intense at work, some weekends I'd rather bake. The side projects that survived were ones I could pick up after a 3-week break and understand in 10 minutes.",
      "Time-boxing works better than daily goals. I don't say 'work on project for 1 hour every day.' I say 'this project gets 3 hours this weekend - no guilt if it doesn't happen other days.'",
      "This site is technically a side project. It exists because I made the rules simple: one post when I have something worth saying. One recipe when I've made it at least twice. No publishing schedule. No pressure.",
      "Your side project should make you feel good, not anxious. The moment it starts feeling like a second job, reset the scope.",
    ],
  },
  {
    id: "d4",
    slug: "git-commands-i-actually-use",
    title: "Git Commands I Actually Use (Beyond the Basics)",
    emoji: "🌿",
    category: "Tools",
    tags: ["git", "terminal", "workflow", "tools"],
    date: "Jan 2026",
    featured: false,
    readTime: "6 min read",
    description:
      "Beyond add, commit, push - the intermediate commands that saved me from disasters.",
    color: "#e8ffe8",
    accentColor: "#2a8a3a",
    content: [
      "git add -p - patch mode. Lets you stage specific chunks of a file instead of the whole thing. Essential when you've made multiple unrelated changes and want clean, atomic commits.",
      "git stash push -m 'description' - named stashes. Stashing without a name is chaos. You'll have 6 stashes and no idea what's in them. Name your stashes.",
      "git log --oneline --graph --all - the visualiser. Shows your branch history as an ASCII graph. I have this aliased to git tree.",
      "git bisect - the bug time machine. Binary search through your commits to find exactly which commit introduced a bug. Sounds nerdy, is actually magic.",
      "git reflog - the undo button for everything. Every operation Git has performed is in the reflog, including things you thought were deleted. If you've ever lost work, start here.",
      "git commit --fixup and git rebase --autosquash - my favourite combo. Make a fixup commit, then automatically squash it into the right parent commit during rebase. Keeps history clean without manual squashing.",
    ],
  },
  {
    id: "d5",
    slug: "how-baking-made-me-better-developer",
    title: "How Baking Made Me a Better Developer",
    emoji: "🧁",
    category: "Opinion",
    tags: ["baking", "mindset", "debugging", "patience"],
    date: "Dec 2025",
    featured: true,
    readTime: "3 min read",
    description:
      "I used to rush everything - baking taught me why some things only work when you give them time.",
    color: "#fde8f8",
    accentColor: "#c43fa0",
    content: [
      "I used to be a very impatient person. I wanted everything instantly - quick results, quick fixes, no waiting. If something didn’t work right away, I’d try to force it or rush through it.",
      "Baking changed that for me.",
      "The first time I really got into it, I realized how much of it depends on timing. You can’t rush dough to rise. You can’t skip resting periods. You can’t speed up lamination and still expect perfect layers. When I tried to hurry things, the result showed it - flat, dense, not what it was supposed to be.",
      "That’s when it clicked: some things only work when you give them time.",
      "It made me rethink how I approach everything else, especially coding. I used to jump straight to fixing without understanding the problem fully. Now I slow down more. I let things process. I test properly. I don’t try to force outcomes.",
      "Baking taught me that you don’t always need to do more - you need to do things at the right pace.",
      "Sometimes the best thing you can do is step back and let the process work.",
    ],
  },
];

export default devPosts;
